import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
/**
 * RmsStateContext
 * ----------------
 * Purpose:
 *  - Provide a lightweight, in-memory cache and prefetch/warm API for RMS flows.
 *  - Dramatically reduce perceived latency by:
 *      1) Returning cached data instantly for repeat visits within a short TTL
 *      2) Parallelizing network calls and pre-warming Cloud Functions endpoints
 *      3) Offering explicit prefetch hooks that pages can call on hover/navigation
 *
 * Design:
 *  - We intentionally keep the cache in-memory (per tab) to avoid stale cross‑session data.
 *  - TTLs are short and conservative; background refresh (SWR style) keeps data fresh.
 *  - All methods are safe to call multiple times; concurrent calls dedupe via in-flight guards.
 *
 * Notes:
 *  - We avoid storing PII in this cache beyond what pages already render.
 *  - For forms, we cache a "bundle" { schema, answers, progress } keyed by (org|patient|formKey).
 */
import {
  // Schema fetching (same endpoint for pre/post account flows)
  apiGetFormSchema,
  // Post-account
  apiCreateFormInstance,
  apiGetFormInstance,
  apiListUserReferrals,
  apiGetPostFormBundle,
  // Pre-account
  apiCreateIntent,
  apiGetIntentState,
} from '../utils/rmsApi';
import { HFP_V4_PORTAL_FORM_KEY } from '../utils/referralStatus';

const DEFAULT_REFERRALS_TTL_MS = 30 * 1000; // 30s – fast moving list; cheap to refresh
const DEFAULT_SCHEMA_TTL_MS = 5 * 60 * 1000; // 5m – schemas change rarely
const DEFAULT_FORM_BUNDLE_TTL_MS = 2 * 60 * 1000; // 2m – answers move while editing; short TTL

const RmsStateContext = createContext(null);

export function RmsStateProvider({ children }) {
  // Referrals cache per org
  const referralsCacheRef = useRef(new Map()); // orgId -> { rows, lastLoadedAt, inFlight?: Promise }
  // Schema cache per formKey (org-agnostic; live pointer is org-scoped but commonly shared)
  const schemaCacheRef = useRef(new Map()); // `${orgId}|${formKey}` -> { schema, version, lastLoadedAt, inFlight?: Promise }
  // Form bundle cache per org/patient/form
  const formBundleRef = useRef(new Map()); // `${orgId}|${patientRecordId}|${formKey}` -> { bundle, lastLoadedAt, inFlight?: Promise }
  // Pre-account intent bundle cache
  const preIntentBundleRef = useRef(new Map()); // `${orgId}|${formKey}|${prospectId}` -> { bundle, lastLoadedAt, inFlight?: Promise }

  const [versionTick, setVersionTick] = useState(0); // trigger subscribers when caches change
  const bump = () => setVersionTick(t => t + 1);

  /**
   * updateCachedFormBundleAnswers
   * -----------------------------
   * Why this exists:
   * - Many RMS pages optimistically read from this in-memory cache first to make navigation feel instant.
   * - We also use a short TTL (2 minutes) to avoid hammering the backend while a user is editing.
   *
   * The problem we are solving:
   * - If a form is auto-completed, then a user edits answers and hits "Submit",
   *   we *do* send the edited answers to the backend.
   * - However, if we don't also update this cache, navigating back into the same form shortly after
   *   can re-hydrate from the stale cached bundle (still containing the auto-complete answers),
   *   making it appear as if the user's edits were reverted.
   *
   * Design notes (kept intentionally simple):
   * - We apply a shallow merge at the questionId level because RMS answers are stored as
   *   top-level keys keyed by questionId (values may be primitives or objects).
   * - We DO NOT attempt a deep merge; patches are expected to contain whole values for a given key.
   * - This is a best-effort UX cache update; the backend remains the source of truth.
   */
  const updateCachedFormBundleAnswers = useCallback(({ orgId, patientRecordId, formKey, instanceId, patch, sectionProgress, initialStepId } = {}) => {
    if (!orgId || !patientRecordId || !formKey) return;
    const iid = instanceId || formKey;
    const key = `${orgId}|${patientRecordId}|${formKey}|${iid}`;
    const existing = formBundleRef.current.get(key);
    const existingBundle = existing?.bundle || {};
    const existingAnswers = existingBundle?.answers || {};
    const patchObj = (patch && typeof patch === 'object') ? patch : {};

    const nextBundle = {
      ...existingBundle,
      // Preserve schema/prefillData unless explicitly replaced by caller
      answers: {
        ...existingAnswers,
        ...patchObj,
      },
    };

    if (Array.isArray(sectionProgress)) {
      nextBundle.sectionProgress = sectionProgress;
    }
    if (initialStepId) {
      nextBundle.initialStepId = initialStepId;
    }

    formBundleRef.current.set(key, {
      ...(existing || {}),
      bundle: nextBundle,
      lastLoadedAt: Date.now(),
      inFlight: null,
    });
    bump();
  }, []);

  const getCachedReferrals = useCallback((orgId) => {
    const entry = referralsCacheRef.current.get(orgId);
    return entry ? entry.rows || [] : [];
  }, []);

  // Facts about the list that aren't rows. invitationRetired: the signed-in
  // email reaches no record because its invitation was retired (an old invite
  // link still signs the person in), so the empty state can say the
  // invitation is no longer valid rather than "no children linked" (TDI-803).
  const getCachedReferralsMeta = useCallback((orgId) => {
    const entry = referralsCacheRef.current.get(orgId);
    return (entry && entry.meta) || {};
  }, []);

  const ensureReferrals = useCallback(async ({ orgId, force = false, ttlMs = DEFAULT_REFERRALS_TTL_MS } = {}) => {
    if (!orgId) return [];
    const now = Date.now();
    const existing = referralsCacheRef.current.get(orgId);
    const fresh = existing && (now - (existing.lastLoadedAt || 0) < ttlMs);
    if (fresh && !force) {
      return existing.rows || [];
    }
    // Deduplicate concurrent calls
    if (existing && existing.inFlight) {
      return existing.inFlight;
    }
    const inflight = (async () => {
      const { referrals, invitationRetired } = await apiListUserReferrals({ orgId });
      const rows = Array.isArray(referrals) ? referrals : [];
      referralsCacheRef.current.set(orgId, {
        rows,
        meta: { invitationRetired: invitationRetired === true },
        lastLoadedAt: Date.now(),
        inFlight: null,
      });
      bump();
      return rows;
    })().finally(() => {
      const e = referralsCacheRef.current.get(orgId);
      if (e) e.inFlight = null;
    });
    referralsCacheRef.current.set(orgId, { ...(existing || {}), inFlight: inflight });
    return inflight;
  }, []);

  const getCachedSchema = useCallback((orgId, formKey) => {
    const entry = schemaCacheRef.current.get(`${orgId}|${formKey}`);
    return entry ? entry.schema : null;
  }, []);

  const ensureSchema = useCallback(async ({ orgId, formKey, force = false, ttlMs = DEFAULT_SCHEMA_TTL_MS } = {}) => {
    if (!orgId || !formKey) return null;
    const key = `${orgId}|${formKey}`;
    const now = Date.now();
    const existing = schemaCacheRef.current.get(key);
    const fresh = existing && (now - (existing.lastLoadedAt || 0) < ttlMs);
    if (fresh && !force) {
      return existing.schema;
    }
    if (existing && existing.inFlight) {
      return existing.inFlight;
    }
    const inflight = (async () => {
      const { schema, version } = await apiGetFormSchema({ orgId, formKey });
      schemaCacheRef.current.set(key, { schema, version, lastLoadedAt: Date.now(), inFlight: null });
      bump();
      return schema;
    })().finally(() => {
      const e = schemaCacheRef.current.get(key);
      if (e) e.inFlight = null;
    });
    schemaCacheRef.current.set(key, { ...(existing || {}), inFlight: inflight });
    return inflight;
  }, []);

  // Pre-account schema (shares same cache map)
  const ensurePreSchema = useCallback(async ({ orgId, formKey, version, force = false, ttlMs = DEFAULT_SCHEMA_TTL_MS } = {}) => {
    if (!orgId || !formKey) return null;
    // The HFP v4 form has no schema document: its page loads its own bundle (TDI-525).
    if (formKey === HFP_V4_PORTAL_FORM_KEY) return null;
    const key = `${orgId}|${formKey}`;
    const now = Date.now();
    const existing = schemaCacheRef.current.get(key);
    const fresh = existing && (now - (existing.lastLoadedAt || 0) < ttlMs);
    if (fresh && !force && !version) {
      return existing.schema;
    }
    if (existing && existing.inFlight) {
      return existing.inFlight;
    }
    const inflight = (async () => {
      const { schema, version: resolved } = await apiGetFormSchema({ orgId, formKey, version });
      schemaCacheRef.current.set(key, { schema, version: resolved, lastLoadedAt: Date.now(), inFlight: null });
      bump();
      return schema;
    })().finally(() => {
      const e = schemaCacheRef.current.get(key);
      if (e) e.inFlight = null;
    });
    schemaCacheRef.current.set(key, { ...(existing || {}), inFlight: inflight });
    return inflight;
  }, []);

  const getCachedFormBundle = useCallback((orgId, patientRecordId, formKey, instanceId) => {
    const key = `${orgId}|${patientRecordId}|${formKey}|${instanceId || formKey}`;
    const entry = formBundleRef.current.get(key);
    return entry ? (entry.bundle || null) : null;
  }, []);

  /**
   * invalidateFormBundle
   * --------------------
   * Clears the cached form bundle for a specific form instance.
   * 
   * When to use:
   *   - After form submission, to ensure the next load fetches fresh data from backend
   *   - When nomination data changes (e.g., guardian email updated) that would affect
   *     prefill or form display
   * 
   * Why this is needed:
   *   The in-memory cache has a 2-minute TTL. If a user submits a form with changes
   *   (e.g., updated parent name/phone), then navigates back to the form within the TTL,
   *   they would see stale cached data instead of their updates. By invalidating the cache,
   *   we force a fresh fetch from Firestore which has the latest data.
   */
  const invalidateFormBundle = useCallback(({ orgId, patientRecordId, formKey, instanceId } = {}) => {
    if (!orgId || !patientRecordId || !formKey) return;
    const iid = instanceId || formKey;
    const key = `${orgId}|${patientRecordId}|${formKey}|${iid}`;
    formBundleRef.current.delete(key);
    bump();
  }, []);

  const ensureFormBundle = useCallback(async ({ orgId, patientRecordId, formKey, instanceId, createIfMissing = false, force = false, ttlMs = DEFAULT_FORM_BUNDLE_TTL_MS } = {}) => {
    if (!orgId || !patientRecordId || !formKey) return null;
    const iid = instanceId || formKey;
    const key = `${orgId}|${patientRecordId}|${formKey}|${iid}`;
    const now = Date.now();
    const existing = formBundleRef.current.get(key);
    const fresh = existing && (now - (existing.lastLoadedAt || 0) < ttlMs);
    if (fresh && !force) {
      return existing.bundle;
    }
    if (existing && existing.inFlight) {
      return existing.inFlight;
    }
    const inflight = (async () => {
      // Preferred path: single-call bundle from backend (with graceful fallback)
      try {
        const b = await apiGetPostFormBundle({ orgId, patientRecordId, formKey, instanceId: iid, createIfMissing });
        const bundle = {
          schema: b?.schema || null,
          // Form status: 'draft' | 'submitted'. Submitted forms should open in review mode.
          // This fixes the bug where revisiting a submitted form shows incomplete sections.
          status: b?.status || 'draft',
          answers: b?.answers || {},
          // Prefill data: suggested answers from previous submissions (e.g., school user's education setting)
          // These are NOT counted as "answered" until the user confirms by clicking Next
          prefillData: b?.prefillData || {},
          sectionProgress: Array.isArray(b?.sectionProgress) ? b.sectionProgress : [],
          // Resume to the step the user was actively viewing (lastStepId), not just the first unanswered.
          // This ensures users return to exactly where they left off, even if they were mid-question
          // or reviewing a completed question. Fall back to lastUnanswered for legacy data.
          initialStepId: b?.lastStepId || b?.lastUnanswered || null,
          // Who started the referral and the caller's role, as the server sees
          // them: the only source for a class teacher, who cannot read the record.
          context: b?.context || null,
          // Whether this respondent's answers are locked, judged by the server
          // with the same rule that refuses the save: the only source for a
          // class teacher, who cannot read the record's lock facts (TDI-754).
          locked: b?.locked === true,
          lockedReason: b?.lockedReason || null,
        };
        formBundleRef.current.set(key, { bundle, lastLoadedAt: Date.now(), inFlight: null });
        bump();
        return bundle;
      } catch (bundleErr) {
        // A call that timed out stalled on the way (TDI-688): the fallback's
        // calls would wait on the same connection, so the page shows its
        // "Try again" now instead.
        if (bundleErr?.code === 'functions/deadline-exceeded') throw bundleErr;
        // A refusal is the server's answer about this caller and this form;
        // the fallback's calls would be refused the same way.
        if (bundleErr?.code === 'functions/permission-denied') throw bundleErr;
        // Fallback path: schema + optimistic getInstance (then create if needed)
        const schemaPromise = ensureSchema({ orgId, formKey, force: false });
        let instance;
        try {
          instance = await apiGetFormInstance({ orgId, patientRecordId, formKey, instanceId: iid });
        } catch (_e) {
          if (createIfMissing) {
          await apiCreateFormInstance({ orgId, patientRecordId, formKey });
            instance = await apiGetFormInstance({ orgId, patientRecordId, formKey, instanceId: iid });
          } else {
            instance = null;
          }
        }
        const schema = await schemaPromise;
        const bundle = {
          schema,
          // Fallback path doesn't have status info; default to 'draft' for safety.
          status: instance?.status || 'draft',
          answers: instance?.answers || {},
          sectionProgress: Array.isArray(instance?.sectionProgress) ? instance.sectionProgress : [],
          // Resume to the step the user was actively viewing (lastStepId), not just the first unanswered.
          initialStepId: instance?.lastStepId || instance?.lastUnanswered || null,
        };
        formBundleRef.current.set(key, { bundle, lastLoadedAt: Date.now(), inFlight: null });
        bump();
        return bundle;
      }
    })().finally(() => {
      const e = formBundleRef.current.get(key);
      if (e) e.inFlight = null;
    });
    formBundleRef.current.set(key, { ...(existing || {}), inFlight: inflight });
    return inflight;
  }, [ensureSchema]);

  // Pre-account: ensure an intent "bundle" for fast initial render
  // The clientToken is required when resuming an existing intent to authenticate API calls
  const ensurePreIntentBundle = useCallback(async ({ orgId, formKey, initiatedBy, intentId, clientToken, idempotencyKey, createIfMissing = false, force = false, ttlMs = DEFAULT_FORM_BUNDLE_TTL_MS } = {}) => {
    if (!orgId || !formKey) return null;
    // Resume existing intent
    if (intentId) {
      const key = `${orgId}|${formKey}|${intentId}`;
      const now = Date.now();
      const existing = preIntentBundleRef.current.get(key);
      const fresh = existing && (now - (existing.lastLoadedAt || 0) < ttlMs);
      if (fresh && !force) {
        return existing.bundle;
      }
      if (existing && existing.inFlight) {
        return existing.inFlight;
      }
      // Use provided clientToken, fall back to cached clientToken from existing bundle, then intentId as last resort
      const effectiveClientToken = clientToken || existing?.bundle?.clientToken || intentId;
      const inflight = (async () => {
        const state = await apiGetIntentState({ orgId, prospectId: intentId, clientToken: effectiveClientToken, formKey });
        // Fetch schema pinned to version (avoid live pointer race)
        const schema = await ensurePreSchema({ orgId, formKey, version: state?.schemaVersion, force: false });
        const bundle = {
          schema,
          prospectId: intentId,
          clientToken: effectiveClientToken,
          answers: state?.answers || {},
          initialStepId: state?.lastStepId || state?.lastUnanswered || null
        };
        preIntentBundleRef.current.set(key, { bundle, lastLoadedAt: Date.now(), inFlight: null });
        bump();
        return bundle;
      })().finally(() => {
        const e = preIntentBundleRef.current.get(key);
        if (e) e.inFlight = null;
      });
      preIntentBundleRef.current.set(key, { ...(existing || {}), inFlight: inflight });
      return inflight;
    }
    // New intent path: optionally create; else just return schema (no writes)
    const startKey = `${orgId}|${formKey}|__new__`;
    const existing = preIntentBundleRef.current.get(startKey);
    if (existing && existing.inFlight) {
      return existing.inFlight;
    }
    const inflight = (async () => {
      // When createIfMissing=false: fetch schema only, do NOT create a prospect
      if (!createIfMissing) {
        const schema = await ensurePreSchema({ orgId, formKey, force: false });
        const bundle = {
          schema,
          prospectId: null,
          clientToken: null,
          answers: {},
          initialStepId: null
        };
        preIntentBundleRef.current.set(startKey, { bundle, lastLoadedAt: Date.now(), inFlight: null });
        bump();
        return bundle;
      }
      // Create new prospect (opt-in)
      const created = await apiCreateIntent({ orgId, formKey, idempotencyKey: idempotencyKey || `${formKey}-${Date.now()}`, initiatedBy });
      const { prospectId, clientToken, schema: retSchema, schemaVersion } = created || {};
      let schema = retSchema || null;
      if (!schema) {
        schema = await ensurePreSchema({ orgId, formKey, version: schemaVersion, force: false });
      } else {
        // populate shared schema cache for later hits
        try {
          schemaCacheRef.current.set(`${orgId}|${formKey}`, { schema, version: schemaVersion, lastLoadedAt: Date.now(), inFlight: null });
        } catch (_) {}
      }
      const bundle = {
        schema,
        prospectId,
        clientToken,
        answers: {},
        initialStepId: null
      };
      // Also hydrate cache under real prospect id for follow-ups
      if (prospectId) {
        preIntentBundleRef.current.set(`${orgId}|${formKey}|${prospectId}`, { bundle, lastLoadedAt: Date.now(), inFlight: null });
      }
      preIntentBundleRef.current.set(startKey, { bundle, lastLoadedAt: Date.now(), inFlight: null });
      bump();
      return bundle;
    })().finally(() => {
      const e = preIntentBundleRef.current.get(startKey);
      if (e) e.inFlight = null;
    });
    preIntentBundleRef.current.set(startKey, { ...(existing || {}), inFlight: inflight });
    return inflight;
  }, [ensurePreSchema]);

  // Prewarm helper: fire cheap calls concurrently to reduce next-route cold starts
  const warmRms = useCallback(async ({ orgId, formKeys = [] } = {}) => {
    try {
      const tasks = [];
      if (orgId) {
        // Warm referrals list
        tasks.push(ensureReferrals({ orgId, force: true }));
      }
      // Warm schemas (common RMS forms) – parallel, best-effort (post-account + pre-account)
      for (const fk of formKeys) {
        tasks.push(ensureSchema({ orgId, formKey: fk, force: true }));
        tasks.push(ensurePreSchema({ orgId, formKey: fk, force: true }));
      }
      await Promise.allSettled(tasks);
    } catch (_e) {
      // non-blocking
    }
  }, [ensureReferrals, ensureSchema, ensurePreSchema]);

  const value = useMemo(() => ({
    // referrals
    getCachedReferrals,
    getCachedReferralsMeta,
    ensureReferrals,
    // schemas
    getCachedSchema,
    ensureSchema,
    ensurePreSchema,
    // form bundles
    getCachedFormBundle,
    ensureFormBundle,
    invalidateFormBundle,
    updateCachedFormBundleAnswers,
    ensurePreIntentBundle,
    // warm
    warmRms,
    // version to allow consumers to re-render
    _version: versionTick,
  }), [getCachedReferrals, getCachedReferralsMeta, ensureReferrals, getCachedSchema, ensureSchema, ensurePreSchema, getCachedFormBundle, ensureFormBundle, invalidateFormBundle, updateCachedFormBundleAnswers, ensurePreIntentBundle, warmRms, versionTick]);

  return (
    <RmsStateContext.Provider value={value}>
      {children}
    </RmsStateContext.Provider>
  );
}

// Developer ergonomics: allow using hooks without an explicit provider (no-op cache).
// This keeps pages working even if the provider is not mounted at the route level yet.
//
// IMPORTANT: this MUST be a module-level constant, not an object literal built
// inside useRmsState(). Consumers put these functions in useEffect dependency
// arrays (the documented pattern — see useRmsPostFormInstance); if the fallback
// were recreated per render, every function would get a new identity on every
// render, re-firing those effects each time (setLoading(true) → render → effect
// → ...) and freezing pages in a permanent loading loop whenever the provider
// is missing. It must also mirror the provider's FULL API: a missing method
// (e.g. ensurePreSchema) is a TypeError crash for consumers that call it.
const NO_PROVIDER_FALLBACK = Object.freeze({
  getCachedReferrals: () => [],
  ensureReferrals: async () => [],
  getCachedSchema: () => null,
  ensureSchema: async () => null,
  ensurePreSchema: async () => null,
  getCachedFormBundle: () => null,
  ensureFormBundle: async () => null,
  invalidateFormBundle: () => {},
  updateCachedFormBundleAnswers: () => {},
  ensurePreIntentBundle: async () => null,
  warmRms: async () => {},
  _version: 0,
});

export function useRmsState() {
  const ctx = useContext(RmsStateContext);
  if (!ctx) {
    return NO_PROVIDER_FALLBACK;
  }
  return ctx;
}

export default RmsStateContext;


